import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, ShieldCheck } from "lucide-react";

import { api } from "../lib/chalkApi";
import { useTeam } from "../contexts/TeamContext";
import { ChalkActionButton, ChalkCard } from "../components/chalk/primitives";

/**
 * /billing/preview-checkout — preview-only stand-in for the real Stripe
 * Checkout cart.
 *
 * Why this page exists: preview environments don't have real Stripe price IDs,
 * so we can't redirect the user to a live Stripe cart. The previous behavior
 * dropped them on /billing/success, which spun on "Finishing your Chalk
 * upgrade…" forever because the webhook never arrived. That read as a bug.
 *
 * Now: backend issues a synthetic session whose URL points here. We show the
 * intended price + a clear "Preview environment" banner, and a "Simulate
 * completed purchase" button that hits the preview-complete endpoint to flip
 * the subscription locally so the user can keep testing gated features.
 */
const TIER_LABELS = {
  chalkboard: { name: "The Chalkboard", price: "$11.99 / month" },
  clipboard_plus: { name: "The Clipboard+", price: "$7.99 / month" },
};

export default function PreviewCheckout() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { refreshTier } = useTeam();
  const sessionId = params.get("session_id");
  const [details, setDetails] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const d = await api.getCheckoutSession(sessionId);
        if (!cancelled) setDetails(d.session);
      } catch (e) {
        if (!cancelled) setError(e.message || "Couldn't load session.");
      }
    }
    if (sessionId) load();
    return () => { cancelled = true; };
  }, [sessionId]);

  async function complete() {
    setBusy(true);
    try {
      await api.previewCompleteCheckout(sessionId);
      await refreshTier();
      navigate(`/billing/success?session_id=${encodeURIComponent(sessionId)}`,
               { replace: true });
    } catch (e) {
      setError(e.message || "Couldn't complete the simulated checkout.");
      setBusy(false);
    }
  }

  function cancel() {
    const last = (() => {
      try { return sessionStorage.getItem("chalk_post_billing_return"); } catch { return null; }
    })();
    navigate(last && last.startsWith("/app") ? last : "/app", { replace: true });
  }

  const tier = details?.tier;
  const interval = details?.billing_interval;
  const label = TIER_LABELS[tier] || { name: tier || "Upgrade", price: "" };

  return (
    <div className="chalk-min-h-screen" style={{
      display: "flex", alignItems: "center", justifyContent: "center",
      background: "var(--chalk-cream, #FBF8F2)", padding: 24,
    }}>
      <ChalkCard data-testid="preview-checkout-card" style={{ maxWidth: 460, width: "100%" }}>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          fontSize: 10, textTransform: "uppercase", letterSpacing: "0.12em",
          color: "#B07B00", fontWeight: 700,
          background: "rgba(255,210,0,0.15)",
          padding: "4px 10px", borderRadius: 999, marginBottom: 14,
        }} data-testid="preview-checkout-badge">
          <ShieldCheck size={12} aria-hidden="true" /> Preview environment
        </div>
        <h1 style={{
          fontFamily: "var(--font-serif, 'DM Serif Display', serif)",
          fontSize: 26, color: "var(--chalk-board)", margin: "0 0 8px",
        }}>
          Confirm your upgrade
        </h1>
        <p style={{ fontSize: 13, color: "var(--chalk-muted)", lineHeight: 1.5, margin: "0 0 18px" }}>
          This preview environment isn't wired to real Stripe yet, so we
          stand in for the cart page here. <strong>No card is charged.</strong>{" "}
          Tap below to simulate a completed purchase — your account flips to
          {" "}{label.name} so you can keep testing gated features.
        </p>

        <div style={{
          background: "#FBF8F2", border: "0.5px solid var(--chalk-border)",
          borderRadius: 12, padding: "14px 16px", marginBottom: 18,
        }} data-testid="preview-checkout-summary">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.10em", color: "var(--chalk-muted)", fontWeight: 700 }}>
                You're upgrading to
              </div>
              <div style={{ fontSize: 18, color: "var(--chalk-board)", fontWeight: 600, marginTop: 4 }}>
                {label.name}
              </div>
              <div style={{ fontSize: 12, color: "var(--chalk-muted)", marginTop: 2 }}>
                {interval === "season" ? "One-time · 120 days · no auto-renew" : interval === "annual" ? "Billed annually" : "Billed monthly"}
                {details?.first_charge_at && <div data-testid="preview-first-charge">First charge on {new Date(details.first_charge_at).toLocaleDateString()}</div>}
              </div>
            </div>
            <div style={{ fontSize: 16, color: "var(--chalk-board)", fontWeight: 700, fontFamily: "var(--font-display, 'Bebas Neue', sans-serif)", letterSpacing: "0.05em" }}>
              {tier === "chalkboard" && interval === "season" ? "$29" : tier === "chalkboard" && interval === "annual" ? "$119/yr" : tier === "clipboard_plus" && interval === "annual" ? "$79/yr" : label.price}
            </div>
          </div>
        </div>

        {error && (
          <div data-testid="preview-checkout-error" style={{
            background: "rgba(176,32,32,0.06)", color: "#B02020",
            fontSize: 12, padding: "10px 12px", borderRadius: 8, marginBottom: 14,
          }}>
            {error}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <ChalkActionButton
            testId="preview-checkout-complete"
            variant="primary"
            label={busy ? "Activating…" : "Simulate completed purchase"}
            loading={busy}
            onClick={complete}
            disabled={!details || busy}
            icon={<CheckCircle2 size={16} aria-hidden="true" />}
          />
          <ChalkActionButton
            testId="preview-checkout-cancel"
            variant="ghost"
            label="Back to Chalk"
            onClick={cancel}
            disabled={busy}
          />
        </div>

        <div style={{ marginTop: 16, fontSize: 11, color: "var(--chalk-muted)", lineHeight: 1.5 }}>
          In production this screen is replaced by the real Stripe-hosted
          card-collection page. Configure <code>STRIPE_API_KEY</code> and the
          <code> STRIPE_PRICE_*</code> envs to enable it.
        </div>
      </ChalkCard>
    </div>
  );
}
